import React from 'react'
import { useQuery } from '@apollo/client'
import {
  trucksListQuery,
  trucksListQueryVariables
} from 'components/TrucksList/types/trucksListQuery'
import QUERY from 'components/TrucksList/query'
import Loading from 'components/Loading'
import PageError from 'components/pages/PageError'
import pathFromUrl from 'utils/pathFromUrl'
import StarsRating from 'components/StarsRating'

type Props = {
  marketName: string
  marketId: number
}

type TruckProps = {
  image: string
  name: string
  rating: trucksListQuery['market']['popularTrucks']['records'][0]['ratingInfo']
  truckPath: string
}

function roundTrucks(total: number): string | null {
  let result: string
  if (total < 10) {
    return null
  }

  if (total < 100) {
    result = `${Math.round(total / 10) * 10}+`
  } else if (total < 1000) {
    result = `${Math.round(total / 100) * 100}+`
  } else {
    result = `${Math.round(total / 1000) * 1000}+`
  }

  return result
}

const TruckCard = ({ image, name, rating, truckPath }: TruckProps) => {
  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden h-full">
      <a href={truckPath}>
        <img
          alt={name}
          className="w-full md:h-48 h-24 object-cover object-center"
          src={image}
        />
      </a>
      <div className="px-1 py-0.5 md:p-2">
        <div>
          <p className="text-gray-900 text-sm font-semibold text-left">
            {name}
          </p>
          {rating && (
            <div className="lg:mb-0 mt-1">
              <div className="flex items-center">
                <StarsRating
                  scale={1.2}
                  stars={Math.round(rating.averageRating)}
                />
                <p className="text-xs md:text-sm font-semibold ml-1 text-left">
                  {Math.round(rating.averageRating * 10) / 10}
                </p>
              </div>
              <p className="text-gray-500 text-xs ml-1 text-left">
                {rating.ratingsCount} reviews
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const TrucksSection = ({ marketName, marketId }: Props) => {
  const { data, error, previousData } = useQuery<
    trucksListQuery,
    trucksListQueryVariables
  >(QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      marketId,
      trucksLimit: 6
    },
    skip: !marketId
  })

  const loading =
    (!data || !data.market?.popularTrucks) && !previousData && !error
  if (loading)
    return (
      <div className="w-full h-72 flex justify-center items-center">
        <Loading />
      </div>
    )
  if (error) return <PageError>{error.message}</PageError>

  const {
    market: {
      popularTrucks: { records: trucks },
      trucks: { total: trucksTotal }
    }
  } = data || previousData!

  if (trucks.length === 0) return null

  return (
    <div className="w-full mx-auto pb-10">
      <div className="max-w-7xl mx-auto md:px-4 sm:px-8">
        <div className="grid grid-cols-3 md:grid-cols-3 md:grid-rows-2 gap-y-4 gap-x-2">
          {trucks.map((truck) => (
            <div key={truck.id} className="">
              <TruckCard
                image={truck.foodImage!.medium}
                name={truck.name}
                rating={truck.ratingInfo}
                truckPath={pathFromUrl(truck.scheduleUrl)}
              />
            </div>
          ))}
        </div>

        {roundTrucks(trucksTotal) && (
          <div className="mt-4 md:mt-8 flex justify-center flex-col items-center gap-y-1">
            <p className="text-xl md:text-3xl text-gray-700 font-light">
              ...and{' '}
              <span className="text-gray-700 font-bold text-xl md:text-3xl">
                {roundTrucks(trucksTotal)}
              </span>{' '}
              more trucks in {marketName}!
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default TrucksSection
